import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import {
  getEbooksByLanguage,
  getTrendingEbooksByLanguage,
  getLatestEbooksByLanguage,
  getPopularEbooksByLanguage
} from '../api/ebooksByLanguage';

/**
 * Hook for fetching ebooks by language with flexible sorting options
 * 
 * @param {string} language - Language to filter by (e.g., 'english', 'spanish')
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {string} options.sort - Sort order ('latest', 'trending', 'popular')
 * @param {boolean} options.uniqueSeries - Whether to return only one book per series
 * @returns {Object} Query results with ebooks and pagination
 */
export const useEbooksByLanguage = (language, {
  page = 1,
  limit = 10,
  sort = 'latest',
  uniqueSeries = false
} = {}) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch,
    isPreviousData
  } = useQuery(
    ['ebooksByLanguage', language, page, limit, sort, uniqueSeries],
    () => getEbooksByLanguage(language, { page, limit, sort, uniqueSeries }),
    {
      onError: (error) => {
        console.error('Query error:', error);
        const errorMessage = error?.response?.data?.error || 'Failed to fetch ebooks by language';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 60000, // 1 minute
      keepPreviousData: true, // Keep previous data while new data is loading
      enabled: language !== undefined && language !== null && language !== '',
    }
  );

  // Extract ebooks and pagination from response
  const ebooks = data?.data || [];
  const pagination = data?.pagination || {
    total: 0,
    pages: 0,
    page: parseInt(page),
    limit: parseInt(limit)
  };

  return {
    ebooks,
    pagination,
    isLoading,
    error,
    isPreviousData,
    refetchEbooks: refetch,
    language: data?.language,
    uniqueSeries: data?.uniqueSeries
  };
};

/**
 * Hook for fetching trending ebooks by language
 * 
 * @param {string} language - Language to filter by
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {boolean} options.uniqueSeries - Whether to return only one book per series
 * @returns {Object} Query results with ebooks and pagination
 */
export const useTrendingEbooksByLanguage = (language, {
  page = 1,
  limit = 10,
  uniqueSeries = false
} = {}) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch,
    isPreviousData
  } = useQuery(
    ['trendingEbooksByLanguage', language, page, limit, uniqueSeries],
    () => getTrendingEbooksByLanguage(language, { page, limit, uniqueSeries }),
    {
      onError: (error) => {
        console.error('Query error:', error);
        const errorMessage = error?.response?.data?.error || 'Failed to fetch trending ebooks';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 60000, // 1 minute
      keepPreviousData: true,
      enabled: language !== undefined && language !== null && language !== '',
    }
  );

  // Extract ebooks and pagination from response
  const ebooks = data?.data || [];
  const pagination = data?.pagination || {
    total: 0,
    pages: 0,
    page: parseInt(page),
    limit: parseInt(limit)
  };

  return {
    ebooks,
    pagination,
    isLoading,
    error,
    isPreviousData,
    refetchEbooks: refetch,
    language: data?.language,
    uniqueSeries: data?.uniqueSeries
  };
};

/**
 * Hook for fetching latest ebooks by language
 * 
 * @param {string} language - Language to filter by
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {boolean} options.uniqueSeries - Whether to return only one book per series
 * @returns {Object} Query results with ebooks and pagination
 */
export const useLatestEbooksByLanguage = (language, {
  page = 1,
  limit = 10,
  uniqueSeries = false
} = {}) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch,
    isPreviousData
  } = useQuery(
    ['latestEbooksByLanguage', language, page, limit, uniqueSeries],
    () => getLatestEbooksByLanguage(language, { page, limit, uniqueSeries }),
    {
      onError: (error) => {
        console.error('Query error:', error);
        const errorMessage = error?.response?.data?.error || 'Failed to fetch latest ebooks';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 60000, // 1 minute
      keepPreviousData: true,
      enabled: language !== undefined && language !== null && language !== '',
    }
  );

  // Extract ebooks and pagination from response
  const ebooks = data?.data || [];
  const pagination = data?.pagination || {
    total: 0,
    pages: 0,
    page: parseInt(page),
    limit: parseInt(limit)
  };

  return {
    ebooks,
    pagination,
    isLoading,
    error,
    isPreviousData,
    refetchEbooks: refetch,
    language: data?.language,
    uniqueSeries: data?.uniqueSeries
  };
};

/**
 * Hook for fetching popular (most liked) ebooks by language
 * 
 * @param {string} language - Language to filter by
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {boolean} options.uniqueSeries - Whether to return only one book per series
 * @returns {Object} Query results with ebooks and pagination
 */
export const usePopularEbooksByLanguage = (language, {
  page = 1,
  limit = 10,
  uniqueSeries = false
} = {}) => {
  const { setError } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    refetch,
    isPreviousData
  } = useQuery(
    ['popularEbooksByLanguage', language, page, limit, uniqueSeries],
    () => getPopularEbooksByLanguage(language, { page, limit, uniqueSeries }),
    {
      onError: (error) => {
        console.error('Query error:', error);
        const errorMessage = error?.response?.data?.error || 'Failed to fetch popular ebooks';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 60000, // 1 minute
      keepPreviousData: true,
      enabled: language !== undefined && language !== null && language !== '',
    }
  );

  // Extract ebooks and pagination from response
  const ebooks = data?.data || [];
  const pagination = data?.pagination || {
    total: 0,
    pages: 0,
    page: parseInt(page),
    limit: parseInt(limit)
  };

  return {
    ebooks,
    pagination,
    isLoading,
    error,
    isPreviousData,
    refetchEbooks: refetch,
    language: data?.language,
    uniqueSeries: data?.uniqueSeries
  };
};